import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from './css/index.module.scss';
import Checkbox from '../../../../ui-kit/Checkbox';
import CompactSelect from '../CompactSelect';
import ChevronIcon from '../../../../assets/icons/dropdownRight.svg';
import useTranslations from '../../../../hooks/useTranslations';
import { DICTIONARY } from '../../../../translations/dictionary';
import { SOUNDPROOFING_GLASS_KINDS, SOUNDPROOFING_THICKNESS_OPTIONS } from '../../../../constants/soundproofing';
import SoundproofingServices from '../../../../services/SoundproofingServices';

const KINDS_LABELS = {
  [SOUNDPROOFING_GLASS_KINDS.single]: DICTIONARY.soundproofingPage.singleGlass,
  [SOUNDPROOFING_GLASS_KINDS.laminated]: DICTIONARY.soundproofingPage.laminatedGlass,
};

export function SettingsCard({ children, className }) {
  return <div className={classNames(styles.card, className)}>{children}</div>;
}

export function ThicknessRow({ options, value, onChange }) {
  const { t } = useTranslations();

  const items = useMemo(() => [
    { value: null, label: t(DICTIONARY.soundproofingPage.any) },
    ...options.map((option) => ({ value: option, label: option })),
  ], [options, t]);

  return (
    <div className={styles.row}>
      <p className={styles.rowTitle}>{t(DICTIONARY.soundproofingPage.thickness)}</p>
      <CompactSelect
        items={items}
        value={value}
        onChange={onChange}
        placeholder={t(DICTIONARY.soundproofingPage.any)}
      />
    </div>
  );
}

// TODO: the logic is assumed from the designs: checking a kind expands it, changing a parameter checks the kind,
//  unchecking resets its parameters. Both kinds can be checked at the same time.
function GlassSettings({ layer, updateLayerParams }) {
  const { t } = useTranslations();
  const [expandedKinds, setExpandedKinds] = useState(() => Object.values(SOUNDPROOFING_GLASS_KINDS)
    .filter((kind) => layer.params[kind].checked));

  const toggleExpanded = (kind) => {
    setExpandedKinds((prev) => (prev.includes(kind) ? prev.filter((i) => i !== kind) : [...prev, kind]));
  };

  const onCheckboxClick = (kind) => {
    const isChecked = layer.params[kind].checked;
    updateLayerParams(layer.id, (params) => ({
      ...params,
      [kind]: isChecked
        ? SoundproofingServices.getDefaultLayerParams(layer.type)[kind]
        : { ...params[kind], checked: true },
    }));
    if (!isChecked && !expandedKinds.includes(kind)) toggleExpanded(kind);
  };

  const changeKindParam = (kind, key, value) => {
    updateLayerParams(layer.id, (params) => ({
      ...params,
      [kind]: { ...params[kind], [key]: value, checked: true },
    }));
  };

  return (
    <div className={styles.list}>
      {Object.values(SOUNDPROOFING_GLASS_KINDS).map((kind) => {
        const kindParams = layer.params[kind];
        const isExpanded = expandedKinds.includes(kind);

        return (
          <SettingsCard key={kind}>
            <div className={styles.cardHeader}>
              <div className={styles.checkbox}>
                <Checkbox
                  text={t(KINDS_LABELS[kind])}
                  value={kindParams.checked}
                  onClick={() => onCheckboxClick(kind)}
                />
              </div>
              <div
                className={classNames(styles.chevron, { [styles.chevronExpanded]: isExpanded }, 'pressable')}
                onClick={() => toggleExpanded(kind)}
              >
                <img src={ChevronIcon} alt=">" />
              </div>
            </div>

            {isExpanded && (
              <div className={styles.cardBody}>
                <ThicknessRow
                  options={SOUNDPROOFING_THICKNESS_OPTIONS[kind]}
                  value={kindParams.thickness}
                  onChange={(value) => changeKindParam(kind, 'thickness', value)}
                />
                {kind === SOUNDPROOFING_GLASS_KINDS.laminated && (
                  <>
                    <div className={styles.checkbox}>
                      <Checkbox
                        text={t(DICTIONARY.soundproofingPage.pvb)}
                        value={kindParams.pvb}
                        onClick={() => changeKindParam(kind, 'pvb', !kindParams.pvb)}
                      />
                    </div>
                    <div className={styles.checkbox}>
                      <Checkbox
                        text={t(DICTIONARY.soundproofingPage.acousticPvb)}
                        value={kindParams.acousticPvb}
                        onClick={() => changeKindParam(kind, 'acousticPvb', !kindParams.acousticPvb)}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </SettingsCard>
        );
      })}
    </div>
  );
}

export default GlassSettings;
